/**
 * TabPicker UI Input Component
 * 
 * Reusable input field components with consistent styling and validation.
 * Provides text, number, and currency input variations.
 * 
 * @module TabPicker.UI.Input
 */

import React, { forwardRef } from 'react';

/**
 * Input component props interface
 * 
 * @interface InputProps
 * @property {string} [className] - Additional CSS classes
 * @property {string} [label] - Input label text
 * @property {string} [error] - Error message to display
 * @property {string} [helpText] - Help text to display below input
 * @property {'text' | 'number' | 'currency'} [variant] - Input type variant
 */
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  variant?: 'text' | 'number' | 'currency';
}

/**
 * Input component for form fields
 * 
 * Provides consistent styling for text and number inputs with
 * label, error, and help text support.
 * 
 * @param {InputProps} props - Component props
 * @returns {JSX.Element} Input element
 * 
 * @example
 * // Basic text input
 * <Input 
 *   label="Game Name"
 *   placeholder="Enter game name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 * />
 * 
 * @example
 * // Currency input with error
 * <Input
 *   variant="currency"
 *   label="Ticket Price"
 *   error={errors.price}
 *   value={price}
 *   onChange={(e) => setPrice(e.target.value)}
 * />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className = '', label, error, helpText, variant = 'text', ...props }, ref) => {
    const baseClasses = 'w-full rounded-xl border bg-white px-3 py-2 outline-none transition-colors duration-200';
    const normalClasses = 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    const errorClasses = 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500';
    
    const inputClasses = `${baseClasses} ${error ? errorClasses : normalClasses} ${className}`;
    
    const inputProps = {
      ...props,
      ref,
      className: inputClasses,
      ...(variant === 'number' && { 
        type: 'number',
        inputMode: 'numeric' as const 
      }),
      ...(variant === 'currency' && { 
        type: 'text',
        inputMode: 'decimal' as const,
        className: `${inputClasses} pl-8` // Space for currency symbol
      })
    };
    
    const inputElement = variant === 'currency' ? (
      <div className="relative">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
          $
        </span>
        <input {...inputProps} />
      </div>
    ) : (
      <input {...inputProps} />
    );
    
    if (!label && !error && !helpText) {
      return inputElement;
    }
    
    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        {inputElement}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        {helpText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helpText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

/**
 * Textarea component props interface
 */
export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helpText?: string;
}

/**
 * Textarea component for multi-line text input
 * 
 * @param {TextareaProps} props - Component props
 * @returns {JSX.Element} Textarea element
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className = '', label, error, helpText, ...props }, ref) => {
    const baseClasses = 'w-full rounded-xl border bg-white px-3 py-2 outline-none transition-colors duration-200 resize-y';
    const normalClasses = 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
    const errorClasses = 'border-red-500 focus:ring-2 focus:ring-red-500 focus:border-red-500';
    
    const textareaClasses = `${baseClasses} ${error ? errorClasses : normalClasses} ${className}`;
    
    const textareaElement = (
      <textarea
        ref={ref}
        className={textareaClasses}
        {...props}
      />
    );
    
    if (!label && !error && !helpText) {
      return textareaElement;
    }
    
    return (
      <div className="w-full">
        {label && (
          <label className="mb-1 block text-sm font-medium text-gray-700">
            {label}
          </label>
        )}
        {textareaElement}
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        {helpText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helpText}</p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

/**
 * Input group component for organizing related inputs
 */
export interface InputGroupProps {
  className?: string;
  children?: React.ReactNode;
}

/**
 * Input group component
 * 
 * @param {InputGroupProps} props - Component props
 * @returns {JSX.Element} Input group element
 */
export function InputGroup({ className = '', children }: InputGroupProps) {
  return (
    <div className={`space-y-4 ${className}`}>
      {children}
    </div>
  );
}